import { ReactNode, useRef } from "react";
import cn from "classnames";
import Button from "../common/Button";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Tooltip from "../common/Tooltip";

interface DropdownProps {
  // Omit the label to render just the trigger + menu (no fieldset/label
  // row), for filter bars and inline controls.
  label?: string | ReactNode;
  size?: "xs" | "sm" | "md" | "lg";
  align?: "left" | "center" | "right";
  id?: string;
  // `disabled` greys the option out and ignores clicks; `tooltip` explains
  // why on hover (shown for disabled and enabled options alike).
  options: Array<{ value: string; label: string; disabled?: boolean; tooltip?: string }>;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  contentClassName?: string;
  // Compact variant for filter bars: smaller trigger text, tighter options
  // that never wrap, and a menu sized to its longest option.
  dense?: boolean;
  // Which way the menu opens. "top" for triggers near the bottom of a
  // clipping container (a modal footer), where a downward menu is cut off.
  direction?: "bottom" | "top";
  // Which edge of the trigger the menu lines up with. Default "end" (right)
  // suits right-aligned rows; use "start" when the trigger sits at the left
  // edge of a clipping container, otherwise the menu opens off-screen.
  menuAlign?: "start" | "end";
}

export default function Dropdown({
  className,
  label,
  size = "md",
  align = "right",
  id,
  options,
  value,
  onChange,
  contentClassName,
  dense = false,
  direction = "bottom",
  menuAlign = "end",
  ...props
}: DropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  // DaisyUI opens the menu while anything inside `.dropdown` is focused, and
  // a click on the trigger focuses it before `click` fires, so "was it open
  // before this click" has to be captured on mousedown. A second click on
  // the trigger then blurs to close.
  const wasOpenRef = useRef(false);

  function getDropdownSizes() {
    switch (size) {
      case "xs":
        return {
          button: "text-xs px-1 max-w-24",
          content: "w-36 text-xs",
          label: "text-xs",
        };
      case "sm":
        return {
          button: "text-xs md:text-sm px-1 md:px-4 max-w-32 md:max-w-fit",
          content: "w-52 text-sm",
          label: "text-xs md:text-base",
        };
      case "lg":
        return {
          button: "text-base px-6 max-w-fit",
          content: "w-64 text-base",
          label: "text-lg",
        };
      case "md":
      default:
        return {
          button: "text-sm px-4 max-w-fit",
          content: "w-56 text-sm",
          label: "text-base",
        };
    }
  }

  const sizeClasses = getDropdownSizes();

  function getAlignmentClasses() {
    switch (align) {
      case "left":
        return "justify-start";
      case "center":
        return "justify-center";
      case "right":
      default:
        return "justify-between";
    }
  }

  const alignmentClasses = getAlignmentClasses();

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    // Remove focus to close the dropdown
    if (dropdownRef.current) {
      (
        dropdownRef.current.querySelector(".dropdown-content") as HTMLElement
      )?.blur();
    }
  };

  const dropdown = (
        <div className={cn("dropdown", menuAlign === "start" ? "dropdown-start" : "dropdown-end", direction === "top" ? "dropdown-top" : "dropdown-bottom")} ref={dropdownRef}>
          <Button
            variant="outline"
            size={size}
            tabIndex={0}
            className={cn(
              "animate-none flex items-center",
              sizeClasses.button,
              alignmentClasses,
              dense && "!text-xs",
              className
            )}
            onMouseDown={() => {
              wasOpenRef.current = Boolean(dropdownRef.current?.matches(":focus-within"));
            }}
            onClick={(e) => {
              if (wasOpenRef.current) {
                (e.currentTarget as HTMLElement).blur();
                (dropdownRef.current?.querySelector(".dropdown-content") as HTMLElement | null)?.blur();
                wasOpenRef.current = false;
              }
            }}
          >
            <span className="truncate flex-1 text-left">
              {options.find((opt) => opt.value === value)?.label ||
                "Select option"}
            </span>
            <ChevronDownIcon className="size-[18px] flex-shrink-0" />
          </Button>
          <ul
            tabIndex={0}
            className={cn(
              "dropdown-content menu bg-base-100 rounded-lg z-[1] shadow m-0 overflow-y-auto flex-row max-h-52",
              direction === "top" ? "mb-1" : "mt-1",
              dense ? "menu-sm p-1 w-max min-w-full max-w-72 text-xs" : cn("p-2", sizeClasses.content),
              contentClassName
            )}
          >
            {options.map((option) => {
              const row = (
                <p
                  aria-disabled={option.disabled || undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    if (option.disabled) return;
                    handleOptionClick(option.value);
                  }}
                  className={cn(
                    "m-0 font-medium",
                    dense && "whitespace-nowrap !py-1.5 !px-2.5 text-xs",
                    option.disabled
                      ? "opacity-40 cursor-not-allowed hover:bg-transparent"
                      : "hover:bg-gray-100 dark:hover:bg-base-200",
                    option.value === value && "bg-base-300 hover:bg-base-300"
                  )}
                >
                  {option.label}
                </p>
              );
              return (
                <li
                  className={cn("m-0 p-0 w-full", !option.disabled && "hover:cursor-pointer")}
                  key={option.value}
                >
                  {option.tooltip ? (
                    <Tooltip overlay={option.tooltip} placement="left">
                      {row}
                    </Tooltip>
                  ) : (
                    row
                  )}
                </li>
              );
            })}
          </ul>
        </div>
  );

  if (label === undefined) return dropdown;

  return (
    <fieldset className="form-control w-full">
      <div className={cn("label p-0 min-h-10", getAlignmentClasses())}>
        <span className="flex items-center">
          <span className={cn("font-medium", sizeClasses.label)}>{label}</span>
        </span>
        {dropdown}
      </div>
    </fieldset>
  );
}
